/**
 * Permission utility functions for checking user permissions
 */

/**
 * Check if a module is enabled for the user
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name (e.g., 'dashboard', 'templates', 'documents')
 * @returns {boolean} - Whether the module is enabled
 */
export const isModuleEnabled = (permissions, module) => {
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module].enable === true;
};

/**
 * Check if user has read permission for a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @returns {boolean} - Whether user has read permission
 */
export const hasReadPermission = (permissions, module) => {
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module].read === true;
};

/**
 * Check if user has write permission for a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @returns {boolean} - Whether user has write permission
 */
export const hasWritePermission = (permissions, module) => {
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module].write === true;
};

/**
 * Check if user has update permission for a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @returns {boolean} - Whether user has update permission
 */
export const hasUpdatePermission = (permissions, module) => {
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module].update === true;
};

/**
 * Check if user has delete permission for a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @returns {boolean} - Whether user has delete permission
 */
export const hasDeletePermission = (permissions, module) => {
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module].delete === true;
};

/**
 * Check if user has specific permission for a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @param {string} permission - Specific permission (e.g., 'use_template')
 * @returns {boolean} - Whether user has the specific permission
 */
export const hasSpecificPermission = (permissions, module, permission) => {
  if (!permissions || !permissions[module]) {
    return false;
  }
  return permissions[module][permission] === true;
};

/**
 * Get all enabled modules for navigation
 * @param {Object} permissions - User permissions object
 * @returns {Array} - Array of enabled module names
 */
export const getEnabledModules = (permissions) => {
  if (!permissions) {
    return [];
  }

  return Object.keys(permissions).filter(
    (module) => permissions[module] && permissions[module].enable === true
  );
};

/**
 * Check if user can perform a specific action on a module
 * @param {Object} permissions - User permissions object
 * @param {string} module - Module name
 * @param {string} action - Action to check ('read', 'write', 'update', 'delete', or custom)
 * @returns {boolean} - Whether user can perform the action
 */
export const canPerformAction = (permissions, module, action) => {
  if (!permissions || !permissions[module]) {
    return false;
  }

  // First check if module is enabled
  if (!permissions[module].enable) {
    return false;
  }

  // Then check the specific action
  return permissions[module][action] === true;
};

/**
 * Check if user has access to a specific document type for templates
 * @param {Object} permissions - User permissions object
 * @param {string} documentType - Document type to check
 * @returns {boolean} - Whether user has access to the document type
 */
export const hasDocumentTypeAccess = (permissions, documentType) => {
  if (!permissions || !permissions.templates) {
    return false;
  }

  // If templates module is not enabled, no access
  if (!permissions.templates.enable) {
    return false;
  }

  const allowedTypes = permissions.templates.documentTypes || [];

  // If no document types are specified, allow all (backward compatibility)
  if (allowedTypes.length === 0) {
    return true;
  }

  // If documentType is empty or null, treat it as "others" for backward compatibility
  const normalizedDocumentType = !documentType || documentType.trim() === "" 
    ? "others" 
    : documentType.trim();

  // Check if the document type (or "others" for empty types) is in the allowed list
  return allowedTypes.includes(normalizedDocumentType);
};
